import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  getCourses,
  Course,
  GetCoursesParams,
  GetCoursesResponse,
} from "@/api/getCourses";

export type DifficultyLevel = "beginner" | "intermediate" | "advanced";

export interface FilterState {
  search: string;
  difficulty: DifficultyLevel[];
  categories: number[];
  trades: number[];
  jobRoles: number[];
}

export interface PaginationState {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  hasMore: boolean;
}

export interface CourseDirectoryState {
  courses: Course[];
  filters: FilterState;
  pagination: PaginationState;
  loading: boolean;
  error: string | null;
  scrollPosition: number;
  lastFetchParams: GetCoursesParams | null;
}

const initialState: CourseDirectoryState = {
  courses: [],
  filters: {
    search: "",
    difficulty: [],
    categories: [],
    trades: [],
    jobRoles: [],
  },
  pagination: {
    page: 1,
    limit: 20,
    totalItems: 0,
    totalPages: 0,
    hasMore: true,
  },
  loading: false,
  error: null,
  scrollPosition: 0,
  lastFetchParams: null,
};

const courseDirectorySlice = createSlice({
  name: "courseDirectory",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setCourses: (state, action: PayloadAction<Course[]>) => {
      state.courses = action.payload;
    },
    appendCourses: (state, action: PayloadAction<Course[]>) => {
      state.courses = [...state.courses, ...action.payload];
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
    },
    setLastFetchParams: (
      state,
      action: PayloadAction<GetCoursesParams | null>,
    ) => {
      state.lastFetchParams = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
      state.pagination.hasMore = true;
    },
    resetState: (state) => {
      Object.assign(state, initialState);
    },
    hydrateFromCache: (
      state,
      action: PayloadAction<Partial<CourseDirectoryState>>,
    ) => {
      const cachedState = action.payload;

      if (cachedState.courses) {
        state.courses = cachedState.courses;
      }
      if (cachedState.filters) {
        state.filters = cachedState.filters;
      }
      if (cachedState.pagination) {
        state.pagination = cachedState.pagination;
      }
      if (cachedState.scrollPosition !== undefined) {
        state.scrollPosition = cachedState.scrollPosition;
      }
      if (cachedState.lastFetchParams) {
        state.lastFetchParams = cachedState.lastFetchParams;
      }
    },
  },
});

/**
 * Async thunk to fetch courses with filters
 * Replaces the current courses list (used for initial load and filter changes)
 */
export const fetchCourses =
  (params?: Partial<GetCoursesParams>): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const state = getState();
      const { filters, pagination } = state.courseDirectory;

      // Build API params from filters and pagination
      const apiParams: GetCoursesParams = {
        page: params?.page ?? 1,
        limit: params?.limit ?? pagination.limit,
        search: filters.search || undefined,
        difficultyLevel:
          filters.difficulty.length === 1 ? filters.difficulty[0] : undefined,
        categoryId:
          filters.categories.length === 1 ? filters.categories[0] : undefined,
        tradeId: filters.trades.length === 1 ? filters.trades[0] : undefined,
        includeArchived: false,
        ...params,
      };

      const response: GetCoursesResponse = await getCourses(apiParams);

      dispatch(setCourses(response.courses));
      dispatch(
        setPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          totalItems: response.pagination.totalItems,
          totalPages: response.pagination.totalPages,
          hasMore: response.pagination.page < response.pagination.totalPages,
        }),
      );
      dispatch(setLastFetchParams(apiParams));
    } catch (error) {
      console.error("Error fetching courses:", error);
      dispatch(
        setError(
          error instanceof Error ? error.message : "Failed to fetch courses",
        ),
      );
      dispatch(
        setPagination({
          hasMore: false,
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

/**
 * Async thunk to fetch more courses for infinite scroll
 * Appends to the existing courses list
 */
export const fetchMoreCourses =
  (): AppThunk => async (dispatch: AppDispatch, getState) => {
    try {
      const state = getState();
      const { filters, pagination, loading } = state.courseDirectory;

      // Prevent duplicate requests
      if (loading || !pagination.hasMore) {
        return;
      }

      dispatch(setLoading(true));
      dispatch(setError(null));

      const nextPage = pagination.page + 1;

      // Build API params from filters and next page
      const apiParams: GetCoursesParams = {
        page: nextPage,
        limit: pagination.limit,
        search: filters.search || undefined,
        difficultyLevel:
          filters.difficulty.length === 1 ? filters.difficulty[0] : undefined,
        categoryId:
          filters.categories.length === 1 ? filters.categories[0] : undefined,
        tradeId: filters.trades.length === 1 ? filters.trades[0] : undefined,
        includeArchived: false,
      };

      const response: GetCoursesResponse = await getCourses(apiParams);

      dispatch(appendCourses(response.courses));
      dispatch(
        setPagination({
          page: response.pagination.page,
          totalItems: response.pagination.totalItems,
          totalPages: response.pagination.totalPages,
          hasMore: response.pagination.page < response.pagination.totalPages,
        }),
      );
      dispatch(setLastFetchParams(apiParams));
    } catch (error) {
      console.error("Error loading more courses:", error);
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to load more courses",
        ),
      );
      dispatch(
        setPagination({
          hasMore: false,
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export const {
  setFilters,
  setCourses,
  appendCourses,
  setLoading,
  setError,
  setPagination,
  setScrollPosition,
  setLastFetchParams,
  resetFilters,
  resetState,
  hydrateFromCache,
} = courseDirectorySlice.actions;

export default courseDirectorySlice.reducer;
